import React, { useEffect, useState } from 'react';
import AllowedEmailsManager from './AllowedEmailsManager';
import SupportedStatesManager from './SupportedStatesManager';
import DocuSignFormsManager from './DocuSignFormsManager';
import SubscriptionAssignmentsManager from './SubscriptionAssignmentsManager';
import UserSwitcherManager from './UserSwitcherManager';
import ListingsManager from './ListingsManager';
import AdminAiRateLimitsManager from './AdminAiRateLimitsManager';
import OwnershipReviewManager from './OwnershipReviewManager';
import LegalDocumentsManager from './LegalDocumentsManager';
import DisclosureEditionsManager from './DisclosureEditionsManager';
import HeldContingenciesManager from './HeldContingenciesManager';
import './AdminHub.css';

const TABS = [
  { id: 'access', label: 'Access Control' },
  { id: 'states', label: 'Document States' },
  // New tab — MLS-grade-listing-data slice 1. Grouped with the document
  // tools rather than access control per design spec §5.1.
  { id: 'listings', label: 'Listings' },
  // Property-ownership verification v1: manual review queue.
  { id: 'ownership-review', label: 'Ownership Review' },
  { id: 'docusign-forms', label: 'DocuSign Forms' },
  // Privacy Notice + Terms of Service versions (drafts, counsel sign-off, publish).
  { id: 'legal', label: 'Legal documents' },
  // Statutory disclosure-form editions (WI RECR / VLDR): view; status only.
  { id: 'disclosure-editions', label: 'Disclosure editions' },
  // WI contingencies held for attorney attention: record what the parties' documents show.
  { id: 'held-contingencies', label: 'Held contingencies' },
  { id: 'subscriptions', label: 'User Subscriptions' },
  { id: 'rate-limits', label: 'AI Rate Limits' },
  { id: 'switch-user', label: 'Switch User' },
];

/**
 * @param {object} props
 * @param {Function} [props.onSwitchUser]
 * @param {string|null} [props.initialTab] - tab to open first (admin deep link)
 * @param {number|null} [props.initialOwnershipCaseId] - case to select in Ownership Review
 * @param {() => void} [props.onDeepLinkHandled] - called once the deep link is applied
 */
export default function AdminHub({
  onSwitchUser,
  initialTab = null,
  initialOwnershipCaseId = null,
  onDeepLinkHandled,
}) {
  const [activeTab, setActiveTab] = useState(() =>
    TABS.some((t) => t.id === initialTab) ? initialTab : 'access',
  );
  // Captured at mount: the parent clears its copy once we report it handled.
  const [ownershipCaseId] = useState(initialOwnershipCaseId);
  useEffect(() => {
    if (initialTab && onDeepLinkHandled) onDeepLinkHandled();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="admin-hub">
      <nav className="admin-hub-tabs" role="tablist" aria-label="Admin sections">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            role="tab"
            aria-selected={activeTab === tab.id}
            aria-controls={`admin-panel-${tab.id}`}
            className={`admin-hub-tab ${activeTab === tab.id ? 'is-active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </nav>

      <div
        className="admin-hub-panel"
        role="tabpanel"
        id={`admin-panel-${activeTab}`}
      >
        {activeTab === 'access' && <AllowedEmailsManager />}
        {activeTab === 'states' && <SupportedStatesManager />}
        {activeTab === 'listings' && <ListingsManager />}
        {activeTab === 'ownership-review' && <OwnershipReviewManager initialCaseId={ownershipCaseId} />}
        {activeTab === 'docusign-forms' && <DocuSignFormsManager />}
        {activeTab === 'legal' && <LegalDocumentsManager />}
        {activeTab === 'disclosure-editions' && <DisclosureEditionsManager />}
        {activeTab === 'held-contingencies' && <HeldContingenciesManager />}
        {activeTab === 'subscriptions' && <SubscriptionAssignmentsManager />}
        {activeTab === 'rate-limits' && <AdminAiRateLimitsManager />}
        {activeTab === 'switch-user' && <UserSwitcherManager onSwitchUser={onSwitchUser} />}
      </div>
    </div>
  );
}
